import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useGetUserFollowersQuery, useGetUserFollowingQuery } from '../../store/api/usersApi'
import Avatar from './Avatar'
import { DROPDOWN_AUTOFOCUS_DELAY_MS } from '../../lib/config'

type Mode = 'followers' | 'following'

interface FollowListModalProps {
  userId: string
  initialMode: Mode
  followerCount: number
  followingCount: number
  onClose: () => void
}

export default function FollowListModal({
  userId, initialMode, followerCount, followingCount, onClose,
}: FollowListModalProps) {
  const [mode, setMode] = useState<Mode>(initialMode)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [followerCursor, setFollowerCursor] = useState<string | undefined>()
  const [followingCursor, setFollowingCursor] = useState<string | undefined>()
  const scrollRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setFollowerCursor(undefined)
      setFollowingCursor(undefined)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  // Reset state and scroll when switching mode
  useEffect(() => {
    setSearch('')
    setDebouncedSearch('')
    setFollowerCursor(undefined)
    setFollowingCursor(undefined)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    setTimeout(() => searchRef.current?.focus(), DROPDOWN_AUTOFOCUS_DELAY_MS)
  }, [mode])

  const { data: followersData, isFetching: followersFetching } = useGetUserFollowersQuery(
    { id: userId, cursor: followerCursor, search: debouncedSearch || undefined },
    { skip: mode !== 'followers' }
  )
  const { data: followingData, isFetching: followingFetching } = useGetUserFollowingQuery(
    { id: userId, cursor: followingCursor, search: debouncedSearch || undefined },
    { skip: mode !== 'following' }
  )

  const data = mode === 'followers' ? followersData : followingData
  const isFetching = mode === 'followers' ? followersFetching : followingFetching
  const users = data?.data ?? []
  const hasMore = data?.hasMore ?? false
  const nextCursor = data?.nextCursor ?? null

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || isFetching || !hasMore || !nextCursor) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      if (mode === 'followers') setFollowerCursor(nextCursor)
      else setFollowingCursor(nextCursor)
    }
  }, [isFetching, hasMore, nextCursor, mode])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem', animation: 'flFade 0.15s ease',
      }}
    >
      <style>{`
        @keyframes flFade  { from { opacity:0 } to { opacity:1 } }
        @keyframes flSlide { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '16px', width: '100%', maxWidth: '420px',
          height: '520px', display: 'flex', flexDirection: 'column',
          boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
          animation: 'flSlide 0.2s ease', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem 0' }}>
          {/* Mode tabs */}
          <div style={{ display: 'flex', gap: '0.15rem', background: 'var(--bg-elevated)', borderRadius: '10px', padding: '0.2rem' }}>
            {(['followers', 'following'] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  padding: '0.35rem 0.875rem', borderRadius: '8px', border: 'none',
                  background: mode === m ? 'var(--bg-card)' : 'transparent',
                  color: mode === m ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: mode === m ? 600 : 400, fontSize: '0.82rem',
                  cursor: 'pointer', transition: 'all 0.15s',
                  boxShadow: mode === m ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                }}
              >
                {m === 'followers'
                  ? `Followers ${followerCount > 0 ? `(${followerCount >= 1000 ? `${(followerCount / 1000).toFixed(1)}k` : followerCount})` : ''}`
                  : `Following ${followingCount > 0 ? `(${followingCount >= 1000 ? `${(followingCount / 1000).toFixed(1)}k` : followingCount})` : ''}`
                }
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: '0.2rem 0.4rem', borderRadius: '6px' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
          >✕</button>
        </div>

        {/* Search */}
        <div style={{ padding: '0.875rem 1.25rem 0.75rem' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${mode}...`}
              autoFocus
              style={{
                width: '100%', padding: '0.55rem 0.75rem 0.55rem 2.25rem',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.875rem',
                outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)' }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem', padding: '0.1rem' }}
              >✕</button>
            )}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: 'var(--border)', margin: '0 1.25rem' }} />

        {/* List */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0.75rem' }}>

          {/* Initial loading skeleton */}
          {isFetching && users.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem 0' }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.5rem' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--bg-elevated)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 12, width: '45%', background: 'var(--bg-elevated)', borderRadius: 4, marginBottom: 6 }} />
                    <div style={{ height: 10, width: '70%', background: 'var(--bg-elevated)', borderRadius: 4, opacity: 0.6 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isFetching && users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{debouncedSearch ? '🔍' : '👥'}</div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.3rem', fontSize: '0.9rem' }}>
                {debouncedSearch ? `No results for "${debouncedSearch}"` : `No ${mode} yet`}
              </div>
              <div style={{ fontSize: '0.78rem' }}>
                {debouncedSearch ? 'Try a different name' : mode === 'followers' ? 'Nobody follows this account yet' : 'Not following anyone yet'}
              </div>
            </div>
          )}

          {/* User rows */}
          {users.map((u) => {
            const name = u.profile?.displayName ?? 'User'
            return (
              <Link
                key={u.id}
                to={`/profile/${u.id}`}
                onClick={onClose}
                style={{ textDecoration: 'none', display: 'block' }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.55rem 0.5rem', borderRadius: '10px', transition: 'background 0.12s', cursor: 'pointer' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
                >
                  <Avatar name={name} avatarUrl={u.profile?.avatarUrl} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {name}
                    </div>
                    {u.profile?.bio && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '0.1rem' }}>
                        {u.profile.bio}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', flexShrink: 0 }}>›</span>
                </div>
              </Link>
            )
          })}

          {/* Load more spinner */}
          {isFetching && users.length > 0 && (
            <div style={{ textAlign: 'center', padding: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              Loading more…
            </div>
          )}

          {/* End of list */}
          {!isFetching && !hasMore && users.length > 0 && (
            <div style={{ textAlign: 'center', padding: '0.75rem', color: 'var(--text-muted)', fontSize: '0.72rem', opacity: 0.5 }}>
              — end —
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
